
import { createClient } from '@supabase/supabase-js';

// Helper to retrieve environment variables from multiple sources
const getEnv = (key: string) => {
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
    // @ts-ignore
    return import.meta.env[key];
  }
  // @ts-ignore
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    // @ts-ignore
    return process.env[key];
  }
  return '';
};

const supabaseUrl = getEnv('VITE_SUPABASE_URL') || 'https://wzsahkqteseumlikpeuf.supabase.co';
const supabaseAnonKey = getEnv('VITE_SUPABASE_ANON_KEY') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind6c2Foa3F0ZXNldW1saWtwZXVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY3NDA3OTksImV4cCI6MjA4MjMxNjc5OX0.z0bU8O4C8I01CcsYuoFzlPDrJJOibwQMZ16VXrgFrzc';

export let isSupabaseConfigured = !!supabaseUrl && !!supabaseAnonKey && supabaseUrl !== 'undefined' && !supabaseUrl.includes('placeholder');

export const setSupabaseConfigured = (value: boolean) => {
  isSupabaseConfigured = value;
};

if (!isSupabaseConfigured) {
  console.warn("⚠️ Supabase credentials missing! Please check your .env file or Vercel Environment Variables.");
} else {
  console.log("✅ Supabase Connection Initialized");
}

// Initialize the Supabase client
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Disable the lock manager to prevent "Acquiring an exclusive Navigator LockManager lock" timeouts
      // which can occur in iframe environments or when multiple instances are initialized.
      lock: (name: string, acquireTimeout: number, fn: () => Promise<any>) => fn()
    }
  }
);


/*
===============================================================================
SUPABASE ROW LEVEL SECURITY (RLS) POLICIES
===============================================================================

To ensure data security, you must apply the following RLS policies in your 
Supabase SQL Editor. These policies enforce that:
1. Platform Admins can access everything.
2. Headteachers can only access data belonging to their school.
3. Teachers can only access data belonging to their school.

-- Enable RLS on all tables
ALTER TABLE users ENABLE ROW LEVEL SECURITY;
ALTER TABLE schools ENABLE ROW LEVEL SECURITY;
ALTER TABLE students ENABLE ROW LEVEL SECURITY;
ALTER TABLE classes ENABLE ROW LEVEL SECURITY;
ALTER TABLE subjects ENABLE ROW LEVEL SECURITY;
ALTER TABLE academic_years ENABLE ROW LEVEL SECURITY;
ALTER TABLE announcements ENABLE ROW LEVEL SECURITY;
ALTER TABLE site_availability ENABLE ROW LEVEL SECURITY;

-- Helper function to get the current user's role
CREATE OR REPLACE FUNCTION public.user_role() RETURNS text AS $$
  SELECT (auth.jwt() -> 'user_metadata' ->> 'role')::text;
$$ LANGUAGE SQL STABLE;

-- Helper function to get the current user's school_id
CREATE OR REPLACE FUNCTION public.user_school_id() RETURNS text AS $$
  SELECT school_id::text FROM public.users WHERE id::text = auth.uid()::text;
$$ LANGUAGE SQL STABLE;

-------------------------------------------------------------------------------
-- USERS TABLE POLICIES
-------------------------------------------------------------------------------
-- Platform Admins can do everything
CREATE POLICY "Admins can do everything on users" 
ON users FOR ALL USING (public.user_role() = 'platform_admin');

-- Users can read their own profile
CREATE POLICY "Users can read own profile" 
ON users FOR SELECT USING (auth.uid()::text = id::text);

-- Users can insert their own profile during registration
CREATE POLICY "Users can insert own profile" 
ON users FOR INSERT WITH CHECK (auth.uid()::text = id::text);

-- Users in the same school can read each other's profiles
CREATE POLICY "Users can read profiles in same school" 
ON users FOR SELECT USING (school_id::text = public.user_school_id());

-- Headteachers can update users in their own school
CREATE POLICY "Headteachers can update users in same school" 
ON users FOR UPDATE USING (
  public.user_role() = 'school_admin' AND 
  school_id::text = public.user_school_id()
);

-------------------------------------------------------------------------------
-- SCHOOLS TABLE POLICIES
-------------------------------------------------------------------------------
-- Platform Admins can do everything
CREATE POLICY "Admins can do everything on schools" 
ON schools FOR ALL USING (public.user_role() = 'platform_admin');

-- Anyone can read active schools (for registration/login checks)
CREATE POLICY "Anyone can read active schools" 
ON schools FOR SELECT USING (status = 'active');

-- Users can read their own school's data
CREATE POLICY "Users can read own school" 
ON schools FOR SELECT USING (id::text = public.user_school_id() OR user_id::text = auth.uid()::text);

-- Authenticated users can insert their own school during registration
CREATE POLICY "Users can insert own school" 
ON schools FOR INSERT TO authenticated WITH CHECK (auth.uid()::text = user_id::text);

-- Headteachers can update their own school
CREATE POLICY "Headteachers can update own school" 
ON schools FOR UPDATE USING (
  public.user_role() = 'school_admin' AND 
  id::text = public.user_school_id()
);

-------------------------------------------------------------------------------
-- GENERAL SCHOOL DATA (Students, Classes, Subjects, etc.)
-------------------------------------------------------------------------------
-- Example for Students table (apply similar to classes, subjects, etc.)
CREATE POLICY "Admins can do everything on students" 
ON students FOR ALL USING (public.user_role() = 'platform_admin');

CREATE POLICY "Users can read students in same school" 
ON students FOR SELECT USING (school_id::text = public.user_school_id());

CREATE POLICY "Headteachers and Teachers can insert/update students in same school" 
ON students FOR ALL USING (
  (public.user_role() IN ('school_admin', 'teacher')) AND 
  school_id::text = public.user_school_id()
);

===============================================================================
*/